// Visible month-grid calendar — used in the desktop layout's middle column
// of the revealed panel. Always exposed (no extra click to open). On mobile
// the parent swaps this for a native <input type="date"> via CSS visibility
// rules (better touch UX on small screens).
//
// Bounded to [today, today + 90 days] to match the booking horizon used
// elsewhere in the widget.

import { useState } from 'react';
import styles from '../styles/widget.module.css';

interface CalendarProps {
  selectedDate: string | null; // YYYY-MM-DD
  onChange: (date: string | null) => void;
  locationName?: string;
  className?: string;
  /**
   * Set of YYYY-MM-DD dates known to have at least one available slot for the
   * current (location, fittingType) query. When provided and not loading,
   * dates NOT in the set are visually disabled (but still clickable to
   * surface the waitlist CTA). When `null`, no per-date filter is applied.
   */
  availableDates?: Set<string> | null;
  /**
   * True while the availability fetch is in flight after a query change.
   * Calendar renders all cells as visually disabled and unclickable, with a
   * centered spinner overlay so the user can't pick a stale-state date.
   */
  loading?: boolean;
}

const DAY_LABELS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const MONTH_LABELS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function isoDate(d: Date): string {
  // YYYY-MM-DD in local time (avoids UTC drift around midnight)
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export function Calendar({
  selectedDate,
  onChange,
  locationName,
  className = '',
  availableDates,
  loading = false,
}: CalendarProps) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const maxDate = new Date(today.getTime() + 90 * 24 * 60 * 60 * 1000);

  // Start on the selected month or current month
  const initialView = selectedDate ? new Date(selectedDate + 'T00:00:00') : today;
  const [viewMonth, setViewMonth] = useState(new Date(initialView.getFullYear(), initialView.getMonth(), 1));

  const selectedAsDate = selectedDate ? new Date(selectedDate + 'T00:00:00') : null;

  // Build a 6-week grid starting on the Sunday before/including the 1st
  const firstOfMonth = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1);
  const gridStart = new Date(firstOfMonth);
  gridStart.setDate(gridStart.getDate() - gridStart.getDay()); // back to Sunday

  const cells: Date[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(d.getDate() + i);
    cells.push(d);
  }

  const canNavPrev = viewMonth > new Date(today.getFullYear(), today.getMonth(), 1);
  const canNavNext = viewMonth < new Date(maxDate.getFullYear(), maxDate.getMonth(), 1);

  const goPrev = () => {
    if (!canNavPrev) return;
    setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1));
  };
  const goNext = () => {
    if (!canNavNext) return;
    setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1));
  };

  return (
    <div
      className={`${styles.calendar} ${className} ${loading ? styles.calendarLoading : ''}`}
    >
      {loading && (
        <div className={styles.calendarLoadingOverlay} aria-live="polite" aria-busy="true">
          <span className={styles.calendarLoadingSpinner} />
          <span className={styles.calendarLoadingLabel}>Checking availability…</span>
        </div>
      )}
      <div className={styles.calendarHeader}>
        {locationName ? `${locationName.toUpperCase()} CALENDAR` : 'CALENDAR'}
      </div>
      <div className={styles.calendarNav}>
        <button
          type="button"
          className={styles.calendarNavBtn}
          onClick={goPrev}
          disabled={!canNavPrev}
          aria-label="Previous month"
        >
          ‹
        </button>
        <span className={styles.calendarMonthLabel}>
          {MONTH_LABELS[viewMonth.getMonth()].toUpperCase()} {viewMonth.getFullYear()}
        </span>
        <button
          type="button"
          className={styles.calendarNavBtn}
          onClick={goNext}
          disabled={!canNavNext}
          aria-label="Next month"
        >
          ›
        </button>
      </div>
      <div className={styles.calendarGrid}>
        {DAY_LABELS.map((d, i) => (
          <div key={`dl-${i}`} className={styles.calendarDayLabel}>{d}</div>
        ))}
        {cells.map((d) => {
          const inMonth = d.getMonth() === viewMonth.getMonth();
          const outOfRange = d < today || d > maxDate;
          const dateKey = isoDate(d);
          // Per-date availability filter only applies once the fetch has
          // resolved (availableDates is a Set). When null, treat as
          // "unknown — don't filter" so dates stay clickable during the brief
          // load window.
          const noAvail = availableDates instanceof Set && !availableDates.has(dateKey);

          // Two flavors of "disabled" — visually they look the same (greyed),
          // but the behavior differs:
          //   - HARD: outOfRange → truly unclickable, no event fires.
          //   - SOFT: no-availability for the current query → still clickable.
          //     Clicking surfaces the existing no-availability + waitlist CTA
          //     in TimeSlots (the same UX the user gets when filters change
          //     after a date was already picked).
          // Sundays were previously hard-disabled here — several stores DO
          // open Sundays, and closed-Sunday stores grey out correctly via
          // the soft path (/availability returns no slots for them), matching
          // the mobile native date input which never restricted weekdays.
          // While loading, every cell looks and behaves disabled — the user
          // shouldn't be picking dates whose availability state is about to
          // change. The spinner overlay covers the grid.
          const hardDisabled = outOfRange || loading;
          const looksDisabled = hardDisabled || noAvail;
          const selected = selectedAsDate ? sameDay(d, selectedAsDate) : false;
          const isToday = sameDay(d, today);
          const cls = [
            styles.calendarDay,
            inMonth ? '' : styles.calendarDayOutMonth,
            looksDisabled ? styles.calendarDayDisabled : '',
            selected ? styles.calendarDaySelected : '',
            isToday && !selected ? styles.calendarDayToday : '',
          ]
            .filter(Boolean)
            .join(' ');
          return (
            <button
              key={isoDate(d)}
              type="button"
              className={cls}
              disabled={hardDisabled}
              onClick={() => onChange(isoDate(d))}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}
